import {
  collection,
  getDocs,
  limit as fsLimit,
  query,
  doc,
  getDoc,
  DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';

export type SuggestedUser = {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  specialties?: string[];
  verified?: boolean;
  location?: string;
  score?: number;
  reason?: string;
};

type UserProfile = {
  id: string;
  specialties: string[];
  location?: string;
  following: string[];
  followers: string[];
};

// Lightweight in-memory cache (resets on page reload)
type CacheEntry = { ts: number; data: SuggestedUser[] };
const SUGGESTIONS_CACHE = new Map<string, CacheEntry>();
const TTL_MS = 5 * 60 * 1000; // 5 minutes

// Scoring weights
const WEIGHTS = {
  MUTUAL_FOLLOWER: 30,      // High score for friends-of-friends
  SHARED_SPECIALTY: 20,     // Medium-high for common interests
  SHARED_LOCATION: 15,      // Medium for same city/country
  VERIFIED_BONUS: 10,       // Small boost for verified accounts
  POPULARITY_BONUS: 5,      // Small boost for high follower counts
  NEW_USER_BONUS: 8,        // Bonus for recently joined active users
};

/**
 * Smart recommendation algorithm that considers:
 * 1. Mutual connections (friends-of-friends)
 * 2. Shared interests/specialties
 * 3. Location proximity
 * 4. Verification status and popularity
 */
export async function fetchSuggestedUsers(
  max: number = 10,
  excludeUserId?: string
): Promise<SuggestedUser[]> {
  const key = `${excludeUserId || 'anon'}:${max}`;
  const now = Date.now();
  const hit = SUGGESTIONS_CACHE.get(key);
  if (hit && now - hit.ts < TTL_MS) {
    return hit.data.slice(0, max);
  }

  // If no current user, fall back to random selection
  if (!excludeUserId) {
    return fetchRandomUsers(max);
  }

  try {
    // Get current user's profile for personalization
    const currentUser = await getUserProfile(excludeUserId);
    if (!currentUser) {
      return fetchRandomUsers(max, excludeUserId);
    }

    // Fetch candidates
    const candidates = await fetchCandidateUsers(excludeUserId, 100);
    if (candidates.length === 0) {
      return [];
    }

    // Score each candidate
    const scoredCandidates = candidates.map((user) => ({
      ...user,
      score: calculateScore(user, currentUser),
      reason: generateReason(user, currentUser),
    }));

    // Sort by score (highest first), then shuffle slightly for variety
    scoredCandidates.sort((a, b) => b.score - a.score);

    // Add light randomization within score tiers to keep suggestions fresh
    const result = shuffleWithTiers(scoredCandidates, max);

    SUGGESTIONS_CACHE.set(key, { ts: now, data: result });
    return result;
  } catch (error) {
    console.error('Error in smart suggestions:', error);
    return fetchRandomUsers(max, excludeUserId);
  }
}

/**
 * Fetch current user's profile including specialties and connections
 */
async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;

    const data = userDoc.data() as DocumentData;

    // Fetch following list
    const followingSnap = await getDocs(
      query(collection(db, 'users', userId, 'following'))
    );
    const following = followingSnap.docs.map((d) => d.id);

    // Fetch followers list
    const followersSnap = await getDocs(
      query(collection(db, 'users', userId, 'followers'))
    );
    const followers = followersSnap.docs.map((d) => d.id);

    return {
      id: userId,
      specialties: Array.isArray(data.specialties) ? data.specialties : [],
      location: data.location || data.city || data.country,
      following,
      followers,
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

/**
 * Fetch candidate users for scoring
 */
async function fetchCandidateUsers(
  excludeUserId: string,
  limit: number
): Promise<SuggestedUser[]> {
  const q = query(
    collection(db, 'users'),
    fsLimit(limit * 2) // Over-fetch to have candidates after filtering
  );

  const snap = await getDocs(q);
  const candidates: SuggestedUser[] = [];

  snap.forEach((docSnap) => {
    const id = docSnap.id;
    if (id === excludeUserId) return;

    const data = docSnap.data() as DocumentData;

    candidates.push({
      id,
      username: data.username || data.handle || id,
      displayName: data.displayName || data.name || data.username || 'User',
      avatar: data.avatar || data.photoURL || undefined,
      specialties: Array.isArray(data.specialties) ? data.specialties : [],
      verified: Boolean(data.verified),
      location: data.location || data.city || data.country,
    });
  });

  return candidates;
}

/**
 * Calculate a recommendation score for a candidate user
 */
function calculateScore(candidate: SuggestedUser, currentUser: UserProfile): number {
  let score = 0;

  // 1. Mutual connections (friends-of-friends)
  // We'd need to fetch the candidate's followers, which is expensive.
  // For now, use a simplified heuristic based on popularity

  // 2. Shared specialties/interests
  if (candidate.specialties && currentUser.specialties.length > 0) {
    const sharedCount = candidate.specialties.filter((s) =>
      currentUser.specialties.includes(s)
    ).length;
    if (sharedCount > 0) {
      score += WEIGHTS.SHARED_SPECIALTY * Math.min(sharedCount, 3); // Cap at 3 shared
    }
  }

  // 3. Location proximity
  if (candidate.location && currentUser.location) {
    const candidateLoc = candidate.location.toLowerCase();
    const userLoc = currentUser.location.toLowerCase();

    // Exact match
    if (candidateLoc === userLoc) {
      score += WEIGHTS.SHARED_LOCATION;
    }
    // Partial match (city within same country)
    else if (
      candidateLoc.includes(userLoc) ||
      userLoc.includes(candidateLoc)
    ) {
      score += WEIGHTS.SHARED_LOCATION * 0.5;
    }
  }

  // 4. Verification bonus
  if (candidate.verified) {
    score += WEIGHTS.VERIFIED_BONUS;
  }

  // 5. Base score to ensure variety
  score += Math.random() * 10;

  return score;
}

/**
 * Generate a human-readable reason for the recommendation
 */
function generateReason(candidate: SuggestedUser, currentUser: UserProfile): string {
  if (candidate.specialties && currentUser.specialties.length > 0) {
    const shared = candidate.specialties.filter((s) =>
      currentUser.specialties.includes(s)
    );
    if (shared.length > 0) {
      return `Also interested in ${shared[0]}`;
    }
  }

  if (candidate.location && currentUser.location) {
    if (candidate.location.toLowerCase() === currentUser.location.toLowerCase()) {
      return `From ${candidate.location}`;
    }
  }

  if (candidate.verified) {
    return 'Verified account';
  }

  return 'Suggested for you';
}

/**
 * Shuffle results slightly within score tiers to maintain variety
 */
function shuffleWithTiers(
  items: (SuggestedUser & { score: number; reason: string })[],
  max: number
): SuggestedUser[] {
  const tiers: (SuggestedUser & { score: number; reason: string })[][] = [[], [], []];

  // Split into tiers: high (70+), medium (40-69), low (<40)
  items.forEach((item) => {
    if (item.score >= 70) tiers[0].push(item);
    else if (item.score >= 40) tiers[1].push(item);
    else tiers[2].push(item);
  });

  // Shuffle within each tier
  tiers.forEach((tier) => {
    for (let i = tier.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tier[i], tier[j]] = [tier[j], tier[i]];
    }
  });

  // Combine and trim
  const combined = [...tiers[0], ...tiers[1], ...tiers[2]];
  return combined.slice(0, max).map(({ score, ...user }) => ({
    ...user,
    score,
    reason: user.reason,
  }));
}

/**
 * Fallback: Fetch random users (original behavior)
 */
async function fetchRandomUsers(
  max: number,
  excludeUserId?: string
): Promise<SuggestedUser[]> {
  const q = query(
    collection(db, 'users'),
    fsLimit(Math.max(max + 5, Math.ceil(max * 2)))
  );

  const snap = await getDocs(q);
  const items: SuggestedUser[] = [];

  snap.forEach((docSnap) => {
    const data = docSnap.data() as DocumentData;
    const id = docSnap.id;
    if (excludeUserId && id === excludeUserId) return;

    items.push({
      id,
      username: data.username || data.handle || id,
      displayName: data.displayName || data.name || data.username || 'User',
      avatar: data.avatar || data.photoURL || undefined,
      specialties: Array.isArray(data.specialties) ? data.specialties : undefined,
      verified: Boolean(data.verified),
      location: data.location || data.city || data.country,
      reason: 'Suggested for you',
    });
  });

  // Fisher-Yates shuffle
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }

  return items.slice(0, max);
}
